import {FC, useMemo, useState} from 'react';
//formik
import {useFormikContext} from 'formik';
import {AuctionFormValues} from '../../../formConfiguration';
//mui
import {Box} from '@mui/material';
//components
import Input from '@/shared/components/Inputs/Input';
import Modal from '@/shared/components/Modal';
import GreyUploadImage from '@/shared/components/UploadDropzone/GreyUploadImage';
//helpers
import {handleGetDonors} from './helpers';
import {getErrorMessage} from '@/utils/helpers';
import {OrganizationAuctionService} from '@/utils/services';
//constants
import {ADD_DONOR, CLOSE, EDIT_DONOR, FIELD, FIELD_LABEL, PLACEHOLDER, SAVE_CHANGES} from '@/utils/constants';
//styles
import {addDonorModalFormStyle} from './AddDonorModalForm.styles';
import {toast} from 'react-toastify';
import {getAuction, getAuctionSelector} from '@/store/organization-auction';
import {useAppDispatch} from '@/store/hooks';
import {useSelector} from 'react-redux';
import {orgAPI} from '@/core/api';

interface IOrganizationAuctionDonorData {
	id: number;
	name: string;
	logo?: string;
	logoUrl: string | File;
}
interface AddDonorFormProps {
	onClose: () => void;
	editableDonor: IOrganizationAuctionDonorData | null;
	isEditMode?: boolean;
}

const AddDonorModalForm: FC<AddDonorFormProps> = ({onClose, editableDonor, isEditMode}) => {
	const {
		setFieldValue,
		values: {donors},
	} = useFormikContext<AuctionFormValues>();

	const dispatch = useAppDispatch();
	const auctionData = useSelector(getAuctionSelector);

	const [donorLogo, setDonorLogo] = useState<File | string | null>(editableDonor?.logoUrl || null);
	const [donorName, setDonorName] = useState<string>(editableDonor?.name || '');

	const isButtonAddEnable = useMemo(() => !!donorLogo && !!donorName, [donorLogo, donorName]);

	const handleSaveAddDonorModal = () => {
		const newDonors = handleGetDonors(donors, editableDonor, donorLogo, donorName);
		setFieldValue(FIELD.donors, newDonors);
		onClose();
	};

	const handleChangeDonorName = async () => {
		if (!editableDonor) return;
		try {
			await OrganizationAuctionService.updateDonor(editableDonor.id, {
				name: donorName,
				logo: editableDonor?.logo ? editableDonor.logo : '',
			});

			if (!auctionData) return;
			await dispatch(getAuction({id: auctionData?.id, apiInstance: orgAPI})).unwrap();
			onClose();
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const handleLogoChange = (logo: File | null) => setDonorLogo(logo);
	const handleNameChange = (name: string) => setDonorName(name);

	const modalTitle = useMemo(() => (editableDonor ? EDIT_DONOR : ADD_DONOR), [editableDonor]);
	const modalMainButtonTitle = useMemo(() => (editableDonor ? SAVE_CHANGES : ADD_DONOR), [editableDonor]);

	return (
		<Modal
			title={modalTitle}
			closeButtonTitle={CLOSE}
			mainButtonTitle={modalMainButtonTitle}
			onClose={onClose}
			onSave={isEditMode ? handleSaveAddDonorModal : handleChangeDonorName}
			open
			sx={addDonorModalFormStyle.modal}
			isSaveButtonDisable={!isButtonAddEnable}
			isShowCloseButton
			isShowSaveButton
		>
			<Box sx={addDonorModalFormStyle.content}>
				<Box sx={addDonorModalFormStyle.logoWrapper}>
					<GreyUploadImage
						label={FIELD_LABEL.donorLogo}
						onChange={handleLogoChange}
						image={donorLogo}
						parentClassName={addDonorModalFormStyle.logo}
					/>
				</Box>

				<Input
					placeholder={PLACEHOLDER.donorName}
					label={FIELD_LABEL.donorName}
					onChange={handleNameChange}
					value={donorName}
				/>
			</Box>
		</Modal>
	);
};
export default AddDonorModalForm;
